<template>
  <diV>
    <form @submit.prevent="validateBeforeSubmit">
      <div class="columns">
        <div class="column is-flex is-vcentered">
          <b-notification :closable="false">
            {{ $t('compCarbonFormsPurchasesForm.info-1') }}
            <p>
              {{ $t('compCarbonFormsPurchasesForm.info-2') }}
            </p>
          </b-notification>
        </div>
        <div class="column is-5">
          <b-notification :closable="false">
            <div class="level-item has-text-centered">
              <div>
                <p class="heading has-text-carbone">
                  {{ $t('compCarbonFormsPurchasesForm.label-4') }}
                </p>
                <div class="title is-size-3 has-text-carbone">
                  <p v-if="intensity !== null">
                    {{ Math.round(intensity.intensity) | splitNumber }} &plusmn;
                    {{ Math.round(intensity.uncertainty) | splitNumber }}
                    <span class="is-size-5">{{ $t('kg.co2e') }}</span>
                  </p>
                  <p v-else>
                    ...
                    <span class="is-size-5">{{ $t('kg.co2e') }}</span>
                  </p>
                </div>
              </div>
            </div>
          </b-notification>
        </div>
      </div>
      <b-notification :closable="false">
        <div class="columns">
          <div class="column is-3">
            <b-field
              :label="$t('compCarbonFormsPurchasesForm.label-1')"
              :type="{ 'is-danger': errors.has('code') }"
              :message="errors.first('code')"
            >
              <b-autocomplete
                icon="tag"
                name="code"
                clearable
                :placeholder="$t('compCarbonFormsPurchasesForm.label-1') + ' *'"
                :disabled="isSubmitted"
                :value="cpurchase.code"
                :open-on-focus="true"
                :data="filteredCodes"
                v-validate="'required'"
                @typing="reset"
                @input.native="(event) => updateDescription(event.target.value)"
                @select="updateDescription"
              />
            </b-field>
          </div>
          <div class="column">
            <b-field
              :label="$t('compCarbonFormsPurchasesForm.label-2')"
              :type="{ 'is-danger': errors.has('intitule') }"
              :message="errors.first('intitule')"
            >
              <b-autocomplete
                icon="tag"
                name="intitule"
                :disabled="isSubmitted"
                clearable
                :placeholder="$t('compCarbonFormsPurchasesForm.label-2') + ' *'"
                :value="description"
                :open-on-focus="true"
                :data="filteredDescriptions"
                @typing="reset"
                @input.native="(event) => updateCode(event.target.value)"
                @select="updateCode"
              />
            </b-field>
          </div>
          <div class="column is-3">
            <b-field
              :label="$t('compCarbonFormsPurchasesForm.label-3')"
              :type="{ 'is-danger': errors.has('amount') }"
              :message="errors.first('amount')"
            >
              <b-input
                name="amount"
                :disabled="isSubmitted"
                icon="eur"
                v-validate="'required|decimal'"
                maxlength="50"
                v-model="cpurchase.amount"
              ></b-input>
            </b-field>
          </div>
        </div>
      </b-notification>
      <b-notification :closable="false">
        <div class="columns">
          <div class="column is-3">
            <b-field
              :label="$t('compCarbonFormsPurchasesForm.label-5')"
              :disabled="submitted"
            >
              <b-input
                name="amortization"
                icon="clock-o"
                type="number"
                min="1"
                max="100"
                v-validate="'integer'"
                v-model="cpurchase.amortization"
              ></b-input>
            </b-field>
          </div>
          <div class="column">
            <b-field
              :label="$t('compCarbonFormsPurchasesForm.label-6')"
              :disabled="submitted"
            >
              <b-input
                name="info"
                icon="info"
                type="text"
                maxlength="250"
                v-model="cpurchase.info"
              ></b-input>
            </b-field>
          </div>
        </div>
      </b-notification>
      <b-notification v-if="authStore.isAuthenticated" :closable="false">
        <tags-input v-model="cpurchase.tags" :disabled="isSubmitted" />
      </b-notification>
      <hr v-if="!isSimulation" />
      <nav v-if="!isSimulation" class="level">
        <div class="level-left"></div>
        <div class="buttons level-right">
          <b-button class="button" @click="$emit('close')" icon-left="close">
            {{ $t('cancel') }}
          </b-button>
          <button
            v-show="modeCreation"
            :disabled="isSubmitted"
            type="submit"
            class="button is-primary"
          >
            <span class="icon is-small"><i class="fa fa-plus"></i></span>
            <span> {{ $t('add') }} </span>
          </button>
          <button
            v-show="!modeCreation"
            :disabled="isSubmitted"
            type="submit"
            class="button is-primary"
          >
            <span class="icon is-small"><i class="fa fa-refresh"></i></span>
            <span> {{ $t('update') }} </span>
          </button>
        </div>
      </nav>
    </form>
  </diV>
</template>

<script>
import Purchase from '@/models/carbon/Purchase.js'
import { useGHGIStore } from '@/stores/ghgi.js'
import { useAuthenticationStore } from '@/stores/authentication.js'

export default {
  name: 'Form',
  props: {
    purchase: {
      type: Object,
      default: function () {
        return {}
      }
    },
    modeCreation: {
      type: Boolean,
      default: false
    },
    isSimulation: {
      type: Boolean,
      default: false
    }
  },
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    'tags-input': () => import('@/components/core/TagsInput.vue')
  },
  data () {
    return {
      cpurchase: Purchase.createFromObj(this.purchase),
      description: ''
    }
  },
  setup () {
    const ghgiStore = useGHGIStore()
    const authStore = useAuthenticationStore()
    return { ghgiStore, authStore }
  },
  watch: {
    purchase: {
      handler () {
        this.cpurchase = Purchase.createFromObj(this.purchase)
      },
      deep: true
    }
  },
  computed: {
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    intensity: function () {
      if (this.cpurchase.isValid()) {
        return this.cpurchase.getCarbonIntensity(this.ghgiStore.item.year)
      } else {
        return null
      }
    },
    filteredCodes: function () {
      return this.codes.filter((option) => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.cpurchase.code.toLowerCase()) >= 0
        )
      })
    },
    codes: function () {
      return Purchase.getCodes()
    },
    filteredDescriptions: function () {
      return this.descriptions.filter((option) => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.description.toLowerCase()) >= 0
        )
      })
    },
    descriptions: function () {
      return Purchase.getDescriptions()
    }
  },
  methods: {
    validateBeforeSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.cpurchase.clean()
          if (this.modeCreation) {
            this.$emit('add', this.cpurchase)
          } else {
            this.$emit('update', {
              from: this.purchase,
              to: this.cpurchase
            })
          }
        }
      })
    },
    select (code) {
      this.cpurchase.setCode(code)
    },
    updateDescription (code) {
      if (code) {
        this.cpurchase.setCode(code)
        this.description = this.cpurchase.getFactorDescription()
      }
    },
    updateCode (description) {
      if (description) {
        this.description = description
        let newCode = this.cpurchase.getCodeFromDescription(description)
        if (newCode) {
          this.cpurchase.setCode(newCode)
        }
      }
    },
    reset () {
      this.cpurchase.id = null
      this.cpurchase.code = ''
      this.description = ''
    }
  },
  mounted () {
    this.description = this.cpurchase.getFactorDescription()
  }
}
</script>

<style>
.has-text-carbone {
  color: #8e6a9e !important;
}
</style>
